/** @jsx jsx */

import { ReactNode } from 'react';
import { clamp } from 'lodash';
import { Icon } from '@zapier/common-components';
import { useHeadingLevel } from '@zapier/heading-level-context';
import { suffixes } from 'bemify-js';

import { css, jsx } from '@emotion/core';

import buildCommonComponent from '../buildCommonComponent';

const Styles = css`
  &.generic-heading {
    font-size: inherit;
    font-weight: 400;
    font-style: normal;
    text-transform: none;
    color: inherit;
    margin: 0;
    padding: 0;
    letter-spacing: 0;
  }

  &.generic-heading--bold {
    font-weight: 600;
  }

  &.generic-heading--uppercase {
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  &.generic-heading--12 {
    font-size: 1.2rem;
  }

  &.generic-heading--15 {
    font-size: 1.5rem;
  }

  &.generic-heading--16 {
    font-size: 1.6rem;
  }

  &.generic-heading--20 {
    font-size: 2rem;
  }

  &.generic-heading--25 {
    font-size: 2.5rem;
  }

  &.generic-heading--30 {
    font-size: 3rem;
  }

  &.generic-heading--1-and-half-em-top {
    margin-top: 1.5em;
  }

  &.generic-heading--1-em-bottom {
    margin-bottom: 1em;
  }

  &.generic-heading--1-em-right {
    margin-right: 1em;
  }

  &.generic-heading--half-em-bottom {
    margin-bottom: 0.5em;
  }

  &.generic-heading--inline {
    display: inline-block;
  }

  &.generic-heading--flex {
    display: flex;
    align-items: center;
  }

  &.generic-heading--centered {
    text-align: center;
  }

  &.generic-heading--deepspace {
    color: #354147;
  }

  &.generic-heading__icon--before {
    margin-right: 0.5em;
  }

  &.generic-heading__icon--after {
    margin-left: 0.5em;
  }
`;

const buildHeading = buildCommonComponent('generic-heading');
const headingComponents = [
  buildHeading(null, 'h1'),
  buildHeading(null, 'h2'),
  buildHeading(null, 'h3'),
  buildHeading(null, 'h4'),
  buildHeading(null, 'h5'),
  buildHeading(null, 'h6'),
];
export const [
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
] = headingComponents;
export const HeadingIcon = buildHeading('__icon', Icon);
type Props = {
  kind?: typeof suffixes;
  children: ReactNode;
  [x: string]: any;
};
export const SectionHeading = ({ kind, ...props }: Props) => (
  <Heading2 kind={['bold 20 1-em-bottom flex', kind]} {...props}>
    {props.children}
  </Heading2>
);
export function Heading({ children, kind, className }: Props) {
  const { level } = useHeadingLevel();
  const headingLevel = clamp(level, 1, 6);
  const HeadingComponent = headingComponents[headingLevel - 1];
  return (
    <HeadingComponent className={className} css={Styles} kind={kind}>
      {children}
    </HeadingComponent>
  );
}
